import React, { useContext, useEffect, useState } from 'react';
import { Panel } from 'primereact/panel';
import { Formik, Form } from 'formik';
import { Switch, Route, useRouteMatch, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import AddSurveyLibrary from './AddSurveyLibrary';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import AddCompetency from './AddCompetency';
import AddAbility from './AddAbility';
import AddSurveyItems from './AddSurveyItems';
import { Loading } from '../../../components/Loading';
import AddKssTexts from './AddKssTexts';
import CommonService from '../common';
import { INITIAL_KSS_TEXT_VALUES } from '../constants';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';

const ADD_ITEMBANK_SURVEY_CONTENT_CONFIG_QUERY = gql`
  query addItemBankSurveyContentConfigQuery($id: ID!) {
    addSurveyItemBankContentConfig(id: $id) {
      availableLibraries {
        code
        displayName
      }
      availableKssTextTypes
      availableTextLevels {
        kssTextType
        levels
      }
      availableSequences {
        kssTextType
        level
        sequences
      }
    }
  }
`;

const ADD_ITEM_BANK_SURVEY_CONTENT = gql`
  mutation addNewCompetencyContentMutation($input: AddNewCompetencyContentInput!) {
    addNewCompetencyContent(input: $input) {
      abilityCode
    }
  }
`;

const INITIAL_CONFIG = {
  availableLibraries: [],
  availableKssTextTypes: [],
  availableTextLevels: [],
  availableSequences: [],
  kssTextValues: INITIAL_KSS_TEXT_VALUES,
};

const AddNewCompetencyContent = () => {
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [addItemBankSurveyContentMutation] = useMutation(ADD_ITEM_BANK_SURVEY_CONTENT);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const history = useHistory();
  const { path } = useRouteMatch();
  const {
    getLibraryInput,
    getCompetencyInput,
    getAbilityInput,
    getItemsInput,
    getKssTextsInput,
    getHeader,
    getNextKssTextValues,
  } = CommonService();

  const addItemBankSurveyContent = async (values) => {
    const libraryInput = getLibraryInput(values);

    const competencyInput = getCompetencyInput(values);

    const abilityInput = getAbilityInput(values);

    const itemsInput = getItemsInput(values);

    const kssTextsInput = getKssTextsInput(values);

    try {
      setSubmitting(true);

      await addItemBankSurveyContentMutation({
        variables: {
          input: {
            accountId: currentAccount,
            libraryInput,
            competencyInput,
            abilityInput,
            itemsInput,
            kssTextsInput,
          },
        },
      });
      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      setSubmitting(false);
    }
  };

  const { loading, error, data } = useQuery(ADD_ITEMBANK_SURVEY_CONTENT_CONFIG_QUERY, {
    variables: { id: currentAccount },
  });

  const { addSurveyItemBankContentConfig } = data;

  useEffect(() => {
    if (addSurveyItemBankContentConfig) {
      setConfig((previousValue) => ({
        ...addSurveyItemBankContentConfig,
        kssTextValues: previousValue.kssTextValues,
      }));
    }
  }, [addSurveyItemBankContentConfig]);

  if (loading || submitting) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const initialValues = {
    library: {
      code: '',
      displayName: '',
      description: '',
      isPrivate: false,
    },
    competency: {
      code: '',
      displayName: '',
      description: '',
      libraryCode: '',
    },
    ability: {
      code: '',
      displayName: '',
      description: '',
    },
    surveyItems: [],
    kssTexts: [],
  };

  const validationSchema = Yup.object().shape({});

  const processFormValues = (surveyItemValues, values) => {
    const nextKssTextValues = getNextKssTextValues(
      surveyItemValues,
      values,
      config.availableKssTextTypes,
      config.availableTextLevels,
      config.availableSequences,
    );

    setConfig({
      ...config,
      kssTextValues: nextKssTextValues,
    });
  };

  return (
    <div className="brand-detailed-wrapper">
      <div className="wrapper p-grid">
        <div className="p-col-12">
          <Panel header={<div className="update-translations-panel-header">{getHeader()}</div>}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => addItemBankSurveyContent(values)}
            >
              <Form>
                <Switch>
                  <Route
                    exact
                    path={`${path}`}
                    component={() => <AddSurveyLibrary />}
                  />
                  <Route
                    exact
                    path={`${path}/add-competency`}
                    component={() => (
                      <AddCompetency availableLibraries={config.availableLibraries} />
                    )}
                  />
                  <Route
                    exact
                    path={`${path}/add-ability`}
                    component={() => <AddAbility />}
                  />
                  <Route
                    exact
                    path={`${path}/add-survey-items`}
                    component={() => (
                      <AddSurveyItems
                        onSave={() =>
                          history.push(`/accounts/${currentAccount}/add-itembank-survey-content/new/add-kss-texts`)
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`${path}/add-kss-texts`}
                    component={() => (
                      <AddKssTexts
                        availableKssTextTypes={config.availableKssTextTypes}
                        availableTextLevels={config.availableTextLevels}
                        availableSequences={config.availableSequences}
                        kssTextValues={config.kssTextValues}
                        processFormValues={(surveyItemValues, values) => processFormValues(surveyItemValues, values)}
                      />
                    )}
                  />
                </Switch>
              </Form>
            </Formik>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default AddNewCompetencyContent;
